import moment from 'moment';
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { Box, Paper, Typography, CircularProgress, Chip, IconButton, Alert, Button, Grid, Container, Tabs, Tab, Divider } from '@mui/material';
import { CheckCircle as SuccessIcon, Error as ErrorIcon, Schedule as PendingIcon, PlayCircle as RunningIcon, Cancel as CancelledIcon, Refresh as RefreshIcon, ArrowBack as BackIcon, Timeline as TimelineIcon, AccountTree as FlowIcon, ViewList as ListIcon } from '@mui/icons-material';

import { getExecutionDetails } from '../../store/Workflows';
import websocketService from '../../services/websocket.service';

// Import our new components
import ExecutionFlowDiagram from './components/ExecutionFlowDiagram';
import { getNodeDefinitionService } from './WorkflowBuilder/nodeService';

const ExecutionMonitor = ({ execution: propExecution, workflowData }) => {
  const { executionId: routeExecutionId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  // Support both prop-based and route-based execution
  const executionId = propExecution?._id || routeExecutionId;
  const [execution, setExecution] = useState(propExecution || null);
  const [nodeLogs, setNodeLogs] = useState({});
  const [currentRunningNode, setCurrentRunningNode] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedNode, setSelectedNode] = useState(null);
  const [loading, setLoading] = useState(!propExecution);
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  const { executionDetails, executionDetailsLoading, nodeTools } = useSelector(state => state.Workflows);

  // Helper function to enrich nodes with nodeDefinition from cache (memoized)
  const enrichNodesWithDefinitions = useCallback((nodes) => {
    if (!nodes || !nodeTools?.data) return nodes;

    const nodeDefService = getNodeDefinitionService(nodeTools);
    if (!nodeDefService) return nodes;

    return nodes.map(node => {
      // Skip if already has nodeDefinition
      if (node.data?.nodeDefinition) return node;

      // Get definition from cache
      const nodeDefinition = nodeDefService.getNodeDefinition(node.type);
      if (!nodeDefinition) return node;

      return {
        ...node,
        data: {
          ...node.data,
          nodeDefinition
        }
      };
    });
  }, [nodeTools]);

  useEffect(() => {
    // If execution is passed as prop, check if it has complete workflow data
    if (propExecution) {
      const workflowNodes = propExecution.workflow?.nodes || propExecution.workflowId?.nodes || workflowData?.nodes;
      const hasFullWorkflowData = workflowNodes && workflowNodes.length > 0 &&
                                  workflowNodes[0]?.data?.nodeDefinition !== undefined;
      const hasExecutedNodes = propExecution.executedNodes !== undefined;

      // If missing workflow data OR executed nodes, fetch full details
      if ((!hasFullWorkflowData || !hasExecutedNodes) && executionId) {
        console.log('[ExecutionMonitor] Fetching full execution details for:', executionId);
        // Clear previous data and show loader
        setExecution(null);
        setNodeLogs({});
        setLoading(true);
        dispatch(getExecutionDetails(executionId));
      } else {
        const workflowWithEnrichedNodes = {
          ...(propExecution.workflow || propExecution.workflowId || workflowData),
          nodes: enrichNodesWithDefinitions(workflowNodes)
        };

        const normalizedExecution = {
          ...propExecution,
          workflow: workflowWithEnrichedNodes
        };

        setExecution(normalizedExecution);

        // Build node logs from executed nodes
        if (propExecution.executedNodes) {
          const logs = {};
          propExecution.executedNodes.forEach(node => {
            logs[node.nodeId] = node;
          });
          setNodeLogs(logs);
        }

        setLoading(false);
      }
    } else if (executionId && !propExecution) {
      // Only fetch if we're using route-based execution ID
      setLoading(true);
      dispatch(getExecutionDetails(executionId));
    }
  }, [executionId, propExecution, dispatch, workflowData, enrichNodesWithDefinitions]);

  useEffect(() => {
    if (executionDetails && executionId === executionDetails._id) {
      // Enrich workflow nodes with definitions
      const workflowNodes = executionDetails.workflow?.nodes || executionDetails.workflowId?.nodes || workflowData?.nodes;
      const workflowWithEnrichedNodes = {
        ...(executionDetails.workflow || executionDetails.workflowId || workflowData),
        nodes: enrichNodesWithDefinitions(workflowNodes)
      };

      // Normalize the execution structure
      const normalizedExecution = {
        ...executionDetails,
        workflow: workflowWithEnrichedNodes
      };

      setExecution(normalizedExecution);

      // Build node logs from executed nodes
      if (executionDetails.executedNodes) {
        const logs = {};
        executionDetails.executedNodes.forEach(node => {
          logs[node.nodeId] = node;
        });
        setNodeLogs(logs);
      }

      setLoading(false);
    }
  }, [executionDetails, executionId, workflowData, enrichNodesWithDefinitions]);

  useEffect(() => {
    // Connect to WebSocket for real-time logs
    if (!executionId) return;

    // Wait for workflow namespace to be connected
    const checkConnectionAndJoin = () => {
      if (websocketService.isConnected('workflow')) {
        websocketService.emit('join:execution', executionId, 'workflow');
        return true;
      }
      return false;
    };

    // Try immediately
    if (!checkConnectionAndJoin()) {
      // If not connected, wait a bit and retry
      const retryInterval = setInterval(() => {
        if (checkConnectionAndJoin()) {
          clearInterval(retryInterval);
        }
      }, 1000);

      // Clean up interval on unmount
      return () => {
        clearInterval(retryInterval);
        websocketService.emit('leave:execution', executionId, 'workflow');
      };
    }

    // Listen for real-time updates via notifySubscribers
    const handleWorkflowEvent = (eventType, data) => {
      console.log(`[ExecutionMonitor] Received ${eventType}:`, data);

      switch (eventType) {
        case 'workflow:node:start':
          setCurrentRunningNode(data.nodeId);
          updateNodeLog(data.nodeId, data);
          break;

        case 'workflow:node:complete':
          // For completed nodes, ensure we have the full data including output
          updateNodeLog(data.nodeId, {
            ...data,
            status: 'completed',
            output: data.output // Ensure output is preserved
          });
          if (currentRunningNode === data.nodeId) {
            setCurrentRunningNode(null);
          }
          break;

        case 'workflow:node:error':
          updateNodeLog(data.nodeId, data);
          if (currentRunningNode === data.nodeId) {
            setCurrentRunningNode(null);
          }
          break;

        case 'workflow:node:progress':
          updateNodeLog(data.nodeId, { ...nodeLogs[data.nodeId], ...data });
          break;

        case 'workflow:complete':
          setCurrentRunningNode(null);
          // Refresh execution details to get final state
          dispatch(getExecutionDetails(executionId));
          break;

        case 'workflow:error':
          setCurrentRunningNode(null);
          // Refresh execution details to get final state
          dispatch(getExecutionDetails(executionId));
          break;
      }
    };

    // Subscribe to execution updates
    const unsubscribe = websocketService.subscribeToExecution(executionId, handleWorkflowEvent);

    return () => {
      websocketService.emit('leave:execution', executionId, 'workflow');
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [executionId, dispatch, currentRunningNode, nodeLogs]);

  const updateNodeLog = (nodeId, log) => {
    console.log(`[ExecutionMonitor] Updating node log for ${nodeId}:`, log);
    setNodeLogs(prev => ({
      ...prev,
      [nodeId]: {
        ...prev[nodeId],
        ...log,
        // Ensure output is preserved
        output: log.output !== undefined ? log.output : prev[nodeId]?.output
      }
    }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <SuccessIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'pending':
        return <PendingIcon color="action" />;
      case 'running':
        return <RunningIcon color="primary" />;
      case 'cancelled':
        return <CancelledIcon color="error" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'default';
      case 'running':
        return 'primary';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleNodeClick = (nodeId) => {
    setSelectedNode(nodeId);
    setActiveTab(2); // Switch to details tab
  };

  const handleRefresh = () => {
    dispatch(getExecutionDetails(executionId));
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#dc3545';
      case 'high': return '#fd7e14';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  if (loading || executionDetailsLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Loading execution details...
        </Typography>
      </Box>
    );
  }

  if (!execution) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Execution not found</Alert>
        <Button startIcon={<BackIcon />} onClick={() => history.goBack()} sx={{ mt: 2 }}>
          Back to Workflows
        </Button>
      </Container>
    );
  }

  return (
    <>
      <div className='executions-sec-inner'>
        <div className='title-area'>
          <div className='left-area'>
            <h4>
              {/* <button className='back-btn' onClick={() => history.goBack()}>
                <svg width='8' height='14' viewBox='0 0 8 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M7 13L1 7L7 1' stroke='black' strokeWidth='2' stroke-linecap='round' stroke-linejoin='round' />
                </svg>
              </button> */}
              Workflow Execution Monitor
            </h4>
            <div className='execution-id'>
              {execution.workflow?.name || 'Workflow'} • ID: {executionId}
              <Chip
                icon={getStatusIcon(execution.status)}
                label={execution.status?.toUpperCase()}
                color={getStatusColor(execution.status)}
              />
            </div>
          </div>
          <div className='right-area'>
            <button className='refresh-btns' onClick={handleRefresh}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.9124 16.8149C10.6649 16.8149 10.4324 16.6499 10.3649 16.3949C10.2824 16.0949 10.4624 15.7874 10.7699 15.7049C13.8149 14.9024 15.9374 12.1424 15.9374 8.99242C15.9374 5.16742 12.8249 2.05492 8.99988 2.05492C5.75238 2.05492 3.62238 3.95242 2.62488 5.09992H4.82988C5.13738 5.09992 5.39238 5.35492 5.39238 5.66242C5.39238 5.96992 5.14488 6.23242 4.82988 6.23242H1.50738C1.46988 6.23242 1.40238 6.22492 1.34988 6.20992C1.28238 6.18742 1.22238 6.15742 1.16988 6.11992C1.10238 6.07492 1.04988 6.01492 1.01238 5.94742C0.974878 5.87992 0.944878 5.79742 0.937378 5.71492C0.937378 5.69242 0.937378 5.67742 0.937378 5.65492V2.24992C0.937378 1.94242 1.19238 1.68742 1.49988 1.68742C1.80738 1.68742 2.06238 1.94242 2.06238 2.24992V4.04242C3.28488 2.72992 5.58738 0.937424 8.99988 0.937424C13.4474 0.937424 17.0624 4.55242 17.0624 8.99992C17.0624 12.6599 14.5949 15.8699 11.0549 16.7999C11.0099 16.8074 10.9574 16.8149 10.9124 16.8149Z" fill="#4B5563" />
                <path d="M8.46738 17.0475C8.45238 17.0475 8.43738 17.04 8.42988 17.04C7.61988 16.9875 6.82488 16.8075 6.07488 16.515C5.85738 16.4325 5.70738 16.215 5.71488 15.9825C5.71488 15.915 5.72988 15.8475 5.75238 15.7875C5.86488 15.5025 6.20238 15.36 6.47988 15.465C7.13238 15.72 7.81488 15.87 8.50488 15.9225C8.79738 15.9375 9.02988 16.1925 9.02988 16.4925L9.02238 16.5225C9.00738 16.815 8.75988 17.0475 8.46738 17.0475ZM4.33488 15.435C4.20738 15.435 4.08738 15.39 3.98238 15.315C3.35238 14.805 2.79738 14.2125 2.34738 13.5525C2.27988 13.455 2.24238 13.35 2.24238 13.2375C2.24238 13.05 2.33238 12.8775 2.48988 12.7725C2.73738 12.6 3.09738 12.6675 3.26988 12.9075C3.26988 12.915 3.26988 12.915 3.26988 12.915C3.27738 12.9225 3.28488 12.9375 3.29238 12.945C3.68238 13.5075 4.15488 14.01 4.68738 14.43C4.81488 14.535 4.89738 14.6925 4.89738 14.865C4.89738 14.9925 4.85988 15.12 4.77738 15.225C4.66488 15.36 4.50738 15.435 4.33488 15.435ZM1.82988 11.775C1.58238 11.775 1.36488 11.6175 1.29738 11.385C1.05738 10.6125 0.937378 9.81 0.937378 9V8.9925C0.944878 8.685 1.19238 8.4375 1.49988 8.4375C1.80738 8.4375 2.06238 8.6925 2.06238 9C2.06238 9.705 2.16738 10.395 2.36988 11.0475C2.38488 11.1075 2.39238 11.16 2.39238 11.22C2.39238 11.46 2.23488 11.6775 1.99488 11.7525C1.94238 11.7675 1.88988 11.775 1.82988 11.775Z" fill="#4B5563" />
              </svg>
            </button>
          </div>
        </div>
        {/* Header */}
        <div className='executions-duration-area'>
          <div className='executions-box'>
            <h5>Started</h5>
            <p>{execution.startTime ? moment(execution.startTime).format('LLL') : 'N/A'}</p>
          </div>
          <div className='executions-box'>
            <h5>Duration</h5>
            <p>{execution.executionTime ? `${execution.executionTime}ms` : 'Running...'}</p>
          </div>
          <div className='executions-box'>
            <h5>Trigger</h5>
            <p>{execution.trigger?.type || 'Manual'}</p>
          </div>
          <div className='executions-box'>
            <h5>Nodes Executed</h5>
            <p>{Object.keys(nodeLogs).length} / {execution.workflow?.nodes?.length || 0}</p>
          </div>
          <div className='executions-box'>
            <h5>Tokens Used</h5>
            <p>{execution.langsmith?.totalTokens || execution.performance?.totalTokensUsed || 'N/A'}</p>
          </div>
          <div className='executions-box'>
            <h5>Total Cost</h5>
            <p>${(execution.langsmith?.totalCost || execution.performance?.totalCost || 0).toFixed(8)}</p>
          </div>
          <div className='executions-box'>
            <h5>Errors</h5>
            <p style={{ 
              color: (execution.langsmith?.nodeErrors?.length > 0 || execution.executionErrors?.length > 0) ? '#dc3545' : '#28a745',
              fontSize: '14px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {(() => {
                const errorCount = execution.langsmith?.nodeErrors?.length || execution.executionErrors?.length || 0;
                if (errorCount === 0) return '0';
                
                // Get the first error message
                const firstError = execution.langsmith?.nodeErrors?.[0] || execution.executionErrors?.[0];
                const errorMessage = firstError?.errorMessage || firstError?.error?.message || 'Unknown error';
                
                // Truncate message if too long
                const truncatedMessage = errorMessage.length > 30 
                  ? errorMessage.substring(0, 27) + '...' 
                  : errorMessage;
                
                return `${errorCount}: ${truncatedMessage}`;
              })()}
            </p>
          </div>
          {execution.langsmith?.traceUrl && (
            <div className='executions-box'>
              <h5>LangSmith Trace</h5>
              <a 
                href={execution.langsmith.traceUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  color: '#2196f3',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              >
                View in LangSmith
                <span style={{ fontSize: '12px' }}>→</span>
              </a>
            </div>
          )}
        </div>
        <div className='executions-details-area'>
          {/* <div className='executions-tabs'>
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
              <Tab label="Flow Diagram" icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.74 9.5625H11.25C9.3825 9.5625 8.4375 8.505 8.4375 6.4125V4.26C8.4375 3.495 8.5275 2.4 9.3225 1.8C9.99 1.305 10.95 1.2675 12.3675 1.68C14.2275 2.22 15.78 3.7725 16.32 5.6325C16.7325 7.0425 16.695 8.01 16.2 8.67C15.6 9.4725 14.505 9.5625 13.74 9.5625ZM10.71 2.52C10.4025 2.52 10.1625 2.58 10.005 2.7C9.7125 2.9175 9.57 3.4275 9.57 4.26V6.42C9.57 8.1 10.215 8.445 11.2575 8.445H13.7475C14.5725 8.445 15.0825 8.3025 15.3075 8.01C15.57 7.665 15.5475 6.975 15.255 5.9625C14.82 4.485 13.545 3.2025 12.0675 2.775C11.5125 2.6025 11.0625 2.52 10.71 2.52Z" fill="var(--primary-color)"/><path d="M8.30253 17.0625C7.90503 17.0625 7.50003 17.0325 7.09503 16.965C4.02753 16.47 1.53003 13.98 1.03503 10.9125C0.397534 6.9675 2.94003 3.2475 6.83253 2.4525C7.14003 2.3925 7.43253 2.5875 7.50003 2.8875C7.56003 3.195 7.36503 3.4875 7.06503 3.555C3.77253 4.23 1.61253 7.38 2.16003 10.7325C2.58003 13.3275 4.68753 15.435 7.28253 15.855C10.65 16.395 13.7925 14.2275 14.46 10.92C14.52 10.6125 14.82 10.4175 15.12 10.4775C15.4275 10.5375 15.6225 10.8375 15.5625 11.1375C14.85 14.64 11.79 17.0625 8.30253 17.0625Z" fill="var(--primary-color)"/></svg>} iconPosition="start" />
              <Tab label="Timeline" icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.75 12.5625C3.4425 12.5625 3.1875 12.3075 3.1875 12V6.75C3.1875 6.4425 3.4425 6.1875 3.75 6.1875C4.0575 6.1875 4.3125 6.4425 4.3125 6.75V12C4.3125 12.3075 4.0575 12.5625 3.75 12.5625Z" fill="var(--primary-color)"/><path d="M3.9375 6.9375C2.28 6.9375 0.9375 5.595 0.9375 3.9375C0.9375 2.28 2.28 0.9375 3.9375 0.9375C5.595 0.9375 6.9375 2.28 6.9375 3.9375C6.9375 5.595 5.595 6.9375 3.9375 6.9375ZM3.9375 2.0625C2.9025 2.0625 2.0625 2.9025 2.0625 3.9375C2.0625 4.9725 2.9025 5.8125 3.9375 5.8125C4.9725 5.8125 5.8125 4.9725 5.8125 3.9375C5.8125 2.9025 4.9725 2.0625 3.9375 2.0625Z" fill="var(--primary-color)"/><path d="M3.75 17.0625C2.1975 17.0625 0.9375 15.8025 0.9375 14.25C0.9375 12.6975 2.1975 11.4375 3.75 11.4375C5.3025 11.4375 6.5625 12.6975 6.5625 14.25C6.5625 15.8025 5.3025 17.0625 3.75 17.0625ZM3.75 12.5625C2.82 12.5625 2.0625 13.32 2.0625 14.25C2.0625 15.18 2.82 15.9375 3.75 15.9375C4.68 15.9375 5.4375 15.18 5.4375 14.25C5.4375 13.32 4.68 12.5625 3.75 12.5625Z" fill="var(--primary-color)"/><path d="M14.25 17.0625C12.6975 17.0625 11.4375 15.8025 11.4375 14.25C11.4375 12.6975 12.6975 11.4375 14.25 11.4375C15.8025 11.4375 17.0625 12.6975 17.0625 14.25C17.0625 15.8025 15.8025 17.0625 14.25 17.0625ZM14.25 12.5625C13.32 12.5625 12.5625 13.32 12.5625 14.25C12.5625 15.18 13.32 15.9375 14.25 15.9375C15.18 15.9375 15.9375 15.18 15.9375 14.25C15.9375 13.32 15.18 12.5625 14.25 12.5625Z" fill="var(--primary-color)"/><path d="M13.6276 12.5925C13.3951 12.5925 13.1776 12.45 13.0951 12.2175C12.5476 10.6425 11.0626 9.58499 9.39008 9.58499C9.38258 9.58499 9.38258 9.58499 9.37508 9.58499L6.80258 9.5925C6.79508 9.5925 6.79508 9.5925 6.78758 9.5925C5.14508 9.5925 3.71258 8.4825 3.30008 6.8925C3.22508 6.5925 3.40508 6.28499 3.70508 6.20999C4.00508 6.13499 4.31258 6.315 4.38758 6.615C4.67258 7.71 5.65508 8.47499 6.78758 8.47499H6.79508L9.36758 8.4675C9.37508 8.4675 9.38258 8.4675 9.39008 8.4675C11.5351 8.4675 13.4476 9.82499 14.1526 11.8575C14.2576 12.15 14.1001 12.4725 13.8076 12.57C13.7476 12.5775 13.6876 12.5925 13.6276 12.5925Z" fill="var(--primary-color)"/></svg>} iconPosition="start" />
              <Tab label="Node Details" icon={<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.3141 2.34675C12.6928 2.34675 14.0721 2.36896 15.4503 2.33886C16.3899 2.31863 17.0955 3.03316 17.0408 3.94805C17.026 4.19873 17.0398 4.45731 16.982 4.69812C16.8256 5.3485 16.2468 5.78473 15.5757 5.78473C12.7382 5.78621 9.90082 5.78621 7.0629 5.78473C6.25214 5.78423 5.60619 5.14914 5.59484 4.34035C5.5909 4.07684 5.58596 3.80889 5.62692 3.54982C5.73647 2.85996 6.31382 2.35712 7.01158 2.35268C8.44559 2.34281 9.8796 2.34972 11.3141 2.34972C11.3141 2.34873 11.3141 2.34774 11.3141 2.34675ZM11.2973 4.83135C12.7012 4.83135 14.1051 4.83185 15.5091 4.83086C15.8762 4.83036 16.0775 4.62903 16.0839 4.26189C16.0854 4.16962 16.0844 4.07734 16.0844 3.98506C16.0844 3.46495 15.9201 3.29964 15.403 3.29964C12.6751 3.29964 9.9477 3.29964 7.21983 3.29964C7.15814 3.29964 7.09646 3.29914 7.03527 3.30309C6.75153 3.32184 6.55858 3.51923 6.54871 3.80494C6.54378 3.94015 6.54723 4.07586 6.54723 4.21107C6.54773 4.6463 6.72932 4.83135 7.15962 4.83135C8.53886 4.83185 9.91809 4.83135 11.2973 4.83135Z" fill="var(--primary-color)"/><path d="M11.317 10.7253C9.93778 10.7253 8.55805 10.7238 7.17882 10.7258C6.66364 10.7263 6.21261 10.5728 5.90666 10.1435C5.7754 9.95945 5.65746 9.73097 5.63427 9.51237C5.59479 9.13783 5.57999 8.74404 5.65302 8.37789C5.78872 7.69642 6.39815 7.27944 7.16253 7.27697C8.14157 7.27352 9.1206 7.27599 10.1001 7.27599C11.886 7.27599 13.6718 7.2745 15.4577 7.27648C16.187 7.27747 16.7481 7.63819 16.9455 8.26489C17.098 8.74947 17.0985 9.25231 16.9445 9.7369C16.7456 10.3631 16.185 10.7223 15.4552 10.7238C14.0755 10.7273 12.6962 10.7253 11.317 10.7253ZM11.2968 9.77292C12.6874 9.77292 14.0784 9.77391 15.469 9.77243C15.8811 9.77193 16.0814 9.56912 16.0839 9.16102C16.0844 9.08108 16.0839 9.00114 16.0839 8.9212C16.0839 8.40849 15.9077 8.22985 15.4009 8.22985C12.6745 8.22985 9.94864 8.22985 7.22224 8.23035C7.15464 8.23035 7.08654 8.22936 7.01992 8.23874C6.72285 8.28019 6.55211 8.47609 6.54767 8.7776C6.5457 8.88814 6.54718 8.99917 6.54718 9.1097C6.54718 9.59527 6.72631 9.77391 7.21681 9.77391C8.5768 9.77341 9.93679 9.77292 11.2968 9.77292Z" fill="var(--primary-color)"/><path d="M11.3136 15.6501C9.94031 15.6501 8.5665 15.6274 7.19417 15.6585C6.30002 15.6787 5.53663 15.016 5.5914 14.0384C5.60275 13.8361 5.58992 13.6303 5.62298 13.432C5.74437 12.708 6.33653 12.217 7.07377 12.217C9.89392 12.2161 12.7146 12.2161 15.5347 12.217C16.3549 12.2175 16.9835 12.7954 17.0289 13.6145C17.0457 13.9195 17.0447 14.2353 16.983 14.5319C16.8439 15.1981 16.2512 15.6476 15.5624 15.6491C14.1466 15.6521 12.7299 15.6501 11.3136 15.6501ZM11.3166 14.7031C12.7086 14.7031 14.1002 14.7041 15.4923 14.7021C15.8861 14.7016 16.0805 14.5038 16.0844 14.112C16.0854 14.0009 16.0854 13.8904 16.0854 13.7794C16.0859 13.3589 15.8999 13.1709 15.4799 13.1709C14.1002 13.1704 12.721 13.1709 11.3413 13.1709C9.92452 13.1709 8.50827 13.1699 7.09153 13.1719C6.75647 13.1724 6.55267 13.3782 6.54724 13.7118C6.54576 13.8164 6.54675 13.921 6.54675 14.0256C6.54675 14.5442 6.70762 14.7031 7.23266 14.7031C8.59414 14.7031 9.95561 14.7031 11.3166 14.7031Z" fill="var(--primary-color)"/><path d="M2.83943 15.921C1.80266 15.8865 0.879384 15.0002 0.948469 13.8331C1.01706 12.6779 1.97192 11.8756 3.12613 11.9604C4.17129 12.0374 5.00278 13.0268 4.91692 14.0917C4.83056 15.1616 3.88113 15.9975 2.83943 15.921ZM2.93368 12.9089C2.34744 12.9197 1.88507 13.3846 1.89494 13.9526C1.90431 14.5166 2.37952 14.9775 2.94404 14.9701C3.53127 14.9627 3.97785 14.4924 3.96502 13.8943C3.95417 13.3476 3.48439 12.8985 2.93368 12.9089Z" fill="var(--primary-color)"/><path d="M4.92038 8.99275C4.92285 10.0863 4.03017 10.9883 2.94454 10.9908C1.84658 10.9937 0.945516 10.0981 0.940088 8.99916C0.93466 7.91897 1.84066 7.01346 2.92875 7.01099C4.01783 7.00902 4.91791 7.90465 4.92038 8.99275ZM3.96503 8.99077C3.96503 8.43168 3.50117 7.96288 2.94898 7.96436C2.36522 7.96584 1.89692 8.42378 1.89346 8.9962C1.89001 9.55283 2.36818 10.0345 2.9248 10.0359C3.49969 10.0374 3.96503 9.5701 3.96503 8.99077Z" fill="var(--primary-color)"/><path d="M2.92667 6.04472C1.82427 6.04176 0.933561 5.14365 0.939976 4.04224C0.946391 2.96648 1.85239 2.06986 2.92815 2.0743C4.0345 2.07874 4.92372 2.97043 4.92027 4.07184C4.91632 5.16536 4.02512 6.04768 2.92667 6.04472ZM2.93604 5.08888C3.50945 5.08789 3.96492 4.63291 3.96541 4.06099C3.9659 3.49745 3.50698 3.02274 2.96022 3.02126C2.37349 3.01978 1.89138 3.49252 1.89483 4.06642C1.89829 4.63094 2.3656 5.08986 2.93604 5.08888Z" fill="var(--primary-color)"/></svg>} iconPosition="start" />
            </Tabs>
          </div> */}
          <div className='executions-panel-area'>
            {/* Flow Diagram Tab */}
            <ExecutionFlowDiagram
              nodes={execution?.workflow?.nodes || workflowData?.nodes || []}
              edges={execution?.workflow?.edges || workflowData?.edges || []}
              nodeLogs={nodeLogs}
              currentRunningNode={currentRunningNode}
            />
            {/* {activeTab === 0 && (
              <ExecutionFlowDiagram
                nodes={execution?.workflow?.nodes || execution?.workflowId?.nodes || []}
                edges={execution?.workflow?.edges || execution?.workflowId?.edges || []}
                nodeLogs={nodeLogs}
                currentRunningNode={currentRunningNode}
              />
            )} */}

            {/* Timeline Tab */}
            {/* {activeTab === 1 && (
              <NodeExecutionTimeline
                nodeLogs={nodeLogs}
                onNodeClick={handleNodeClick}
                currentRunningNode={currentRunningNode}
              />
            )} */}

            {/* Node Details Tab */}
            {/* {activeTab === 2 && (
              <Box>
                {selectedNode && nodeLogs[selectedNode] ? (
                  <NodeExecutionDetails
                    log={nodeLogs[selectedNode]}
                    nodeId={selectedNode}
                  />
                ) : (
                  <Alert severity="info">
                    Select a node from the timeline or flow diagram to view details.
                  </Alert>
                )}

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>All Nodes</Typography>
                  {Object.entries(nodeLogs).map(([nodeId, log]) => (
                    <NodeExecutionDetails
                      key={nodeId}
                      log={log}
                      nodeId={nodeId}
                    />
                  ))}
                </Box>
              </Box>
            )} */}
          </div>
          
          {/* Error Details Section */}
          {(execution.langsmith?.nodeErrors?.length > 0 || execution.executionErrors?.length > 0) && (
            <div className='executions-error-details-area' style={{ marginTop: '20px' }}>
              <div className='error-summary-header'>
                <h4>Error Details</h4>
                <button 
                  className='refresh-btns'
                  onClick={() => setShowErrorDetails(!showErrorDetails)}
                  style={{ background: 'none', border: 'none' }}
                >
                  <svg 
                    width="12" 
                    height="12" 
                    viewBox="0 0 12 12" 
                    style={{ 
                      transform: showErrorDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    <path d="M2 4L6 8L10 4" stroke="#666" strokeWidth="2" fill="none"/>
                  </svg>
                </button>
              </div>
              
              {showErrorDetails && (
                <div className='error-details-content' style={{ marginTop: '10px' }}>
                  {/* Error Categories Summary */}
                  {execution.langsmith?.errorCategories?.length > 0 && (
                    <div className='error-categories' style={{ marginBottom: '15px' }}>
                      <h5>Error Summary by Category</h5>
                      <div className='error-categories-list'>
                        {execution.langsmith.errorCategories.map((category, index) => (
                          <div key={index} className='error-category-item' style={{ 
                            padding: '8px 12px', 
                            margin: '5px 0', 
                            backgroundColor: 'var(--sa-dark-surface-light)',
                            borderLeft: `4px solid ${getSeverityColor(category.severity)}`,
                            borderRadius: '4px'
                          }}>
                            <strong style={{ textTransform: 'capitalize' }}>
                              {category.category.replace('_', ' ')}
                            </strong>
                            <span style={{ 
                              marginLeft: '10px',
                              padding: '2px 8px',
                              backgroundColor: getSeverityColor(category.severity),
                              color: 'white',
                              borderRadius: '12px',
                              fontSize: '12px'
                            }}>
                              {category.severity} ({category.count})
                            </span>
                            <div style={{ fontSize: '12px', color: 'var(--sa-dark-text-muted)', marginTop: '4px' }}>
                              Nodes affected: {category.nodeIds.join(', ')}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Detailed Node Errors */}
                  {execution.langsmith?.nodeErrors?.length > 0 && (
                    <div className='detailed-errors'>
                      <h5>Detailed Node Errors</h5>
                      {execution.langsmith.nodeErrors.map((error, index) => (
                        <div key={index} className='error-detail-item' style={{
                          padding: '12px',
                          margin: '8px 0',
                          backgroundColor: 'var(--sa-dark-surface)',
                          border: '1px solid var(--sa-dark-border)',
                          borderRadius: '6px',
                          borderLeft: `4px solid ${getSeverityColor(error.errorSeverity)}`
                        }}>
                          <div className='error-header' style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <strong>{error.nodeName}</strong>
                            <span style={{ 
                              marginLeft: '10px',
                              padding: '2px 6px',
                              backgroundColor: 'var(--sa-dark-surface-light)',
                              borderRadius: '4px',
                              fontSize: '11px'
                            }}>
                              {error.nodeType}
                            </span>
                            <span style={{ 
                              marginLeft: '10px',
                              padding: '2px 8px',
                              backgroundColor: getSeverityColor(error.errorSeverity),
                              color: 'white',
                              borderRadius: '12px',
                              fontSize: '11px'
                            }}>
                              {error.errorSeverity}
                            </span>
                          </div>
                          <div className='error-message' style={{ 
                            backgroundColor: 'var(--sa-dark-surface-light)',
                            padding: '8px',
                            borderRadius: '4px',
                            fontFamily: 'monospace',
                            fontSize: '13px',
                            marginBottom: '8px'
                          }}>
                            {error.errorMessage}
                          </div>
                          <div className='error-metadata' style={{ fontSize: '12px', color: 'var(--sa-dark-text-muted)' }}>
                            <span>Code: {error.errorCode}</span>
                            <span style={{ marginLeft: '15px' }}>
                              Category: {error.errorCategory.replace('_', ' ')}
                            </span>
                            <span style={{ marginLeft: '15px' }}>
                              Time: {new Date(error.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ExecutionMonitor;